import React, { useEffect, useRef, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  SearchIcon,
  ChartIcon,
  PlusIcon,
  ChevronUpIcon,
  FavouriteIcon,
  CopyIcon,
  AddToGroupIcon,
  RefreshIcon,
  ExportIcon,
  DeleteIcon,
} from "./icons";
import { AppInsightsControlsProps } from "./types";

type MenuAction = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  title?: string;
};

/**
 * App-insights keywords toolbar (Figma 804:69588). Pure presentational:
 * a search field + an "Actions" dropdown (Figma 4155:21387: favourite / copy /
 * add to group / update metrics / export, then delete) on the left, a centered
 * "N keyword(s) selected" label, and Chart / Keywords actions on the right.
 * White surfaces flip in dark mode via design tokens.
 */
export const AppInsightsControls: React.FC<AppInsightsControlsProps> = ({
  searchValue,
  searchPlaceholder = "Filter keyword(s)",
  onSearchChange,
  onBookmark,
  onCopy,
  onDelete,
  actionsDisabled = false,
  bookmarkDisabled,
  copyDisabled,
  deleteDisabled,
  bookmarkActive = false,
  copyActive = false,
  onAddToGroup,
  addToGroupDisabled,
  addToGroupTitle,
  onUpdateMetrics,
  updateMetricsDisabled,
  updateMetricsLoading = false,
  updateMetricsTitle,
  onExport,
  selectedCount = 0,
  onChart,
  chartLabel = "Chart",
  chartActive = false,
  onKeywords,
  keywordsLabel = "Keywords",
  onRelevant,
  relevantLabel = "Relevant",
  relevantActive = false,
  className,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Outside click / Escape close the menu.
  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (event: MouseEvent) => {
      if (actionsRef.current?.contains(event.target as Node)) return;
      setMenuOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  const actions: MenuAction[] = [
    {
      key: "favourite",
      label: "Favourite",
      icon: <FavouriteIcon className={styles.icon20} />,
      onClick: onBookmark,
      disabled: bookmarkDisabled,
      active: bookmarkActive,
    },
    {
      key: "copy",
      label: "Copy",
      icon: <CopyIcon className={styles.icon20} />,
      onClick: onCopy,
      disabled: copyDisabled,
      active: copyActive,
    },
    ...(onAddToGroup
      ? [
          {
            key: "add-to-group",
            label: "Add to a group",
            icon: <AddToGroupIcon className={styles.icon20} />,
            onClick: onAddToGroup,
            disabled: addToGroupDisabled,
            title: addToGroupTitle,
          },
        ]
      : []),
    ...(onUpdateMetrics
      ? [
          {
            key: "update-metrics",
            label: "Update metrics",
            icon: (
              <RefreshIcon
                className={cs(styles.icon20, {
                  [styles.iconSpinning]: updateMetricsLoading,
                })}
              />
            ),
            onClick: onUpdateMetrics,
            disabled: updateMetricsDisabled,
            active: updateMetricsLoading,
            title: updateMetricsTitle,
          },
        ]
      : []),
    ...(onExport
      ? [
          {
            key: "export",
            label: "Export",
            icon: <ExportIcon className={styles.icon20} />,
            onClick: onExport,
            disabled: selectedCount === 0 || actionsDisabled,
            title:
              selectedCount > 0
                ? `Export ${selectedCount} keyword${selectedCount === 1 ? "" : "s"} to Excel`
                : "Export selected keywords to Excel",
          },
        ]
      : []),
  ];

  const menuItem = (action: MenuAction, danger = false) => (
    <button
      type="button"
      role="menuitem"
      className={cs(styles.menuItem, {
        [styles.menuItemActive]: action.active,
        [styles.menuItemDanger]: danger,
      })}
      onClick={() => {
        setMenuOpen(false);
        action.onClick?.();
      }}
      disabled={action.disabled ?? actionsDisabled}
      title={action.title}
    >
      {action.icon}
      <span className={styles.menuLabel}>{action.label}</span>
    </button>
  );

  return (
    <div className={cs(styles.bar, className)}>
      <div className={styles.group}>
        <div className={styles.search}>
          <SearchIcon className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            value={searchValue ?? ""}
            placeholder={searchPlaceholder}
            onChange={(e) => onSearchChange?.(e.target.value)}
          />
        </div>

        <div ref={actionsRef} className={styles.actions}>
          <button
            type="button"
            className={cs(styles.actionsButton, {
              [styles.actionsButtonOpen]: menuOpen,
            })}
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            Actions
            <span className={styles.chevronSlot}>
              <ChevronUpIcon
                className={cs(styles.chevron, {
                  [styles.chevronDown]: !menuOpen,
                })}
              />
            </span>
          </button>

          {menuOpen && (
            <div className={styles.menu} role="menu">
              {actions.map((action, idx) => (
                <div
                  key={action.key}
                  className={cs(styles.menuRow, {
                    // divider between the list and the destructive Delete
                    [styles.menuRowDivided]: idx === actions.length - 1,
                  })}
                >
                  {menuItem(action)}
                </div>
              ))}
              <div className={styles.menuRow}>
                {menuItem(
                  {
                    key: "delete",
                    label: "Delete",
                    icon: <DeleteIcon className={styles.icon20} />,
                    onClick: onDelete,
                    disabled: deleteDisabled,
                  },
                  true,
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <span className={styles.selected}>
        {selectedCount} keyword{selectedCount === 1 ? "" : "s"} selected
      </span>

      <div className={cs(styles.group, styles.groupEnd)}>
        {onRelevant && (
          <button
            type="button"
            className={cs(styles.button, styles.buttonOutlined, {
              [styles.buttonOutlinedActive]: relevantActive,
            })}
            onClick={onRelevant}
          >
            <SearchIcon className={styles.icon20} />
            {relevantLabel}
          </button>
        )}
        <button
          type="button"
          className={cs(styles.button, styles.buttonOutlined, {
            [styles.buttonOutlinedActive]: chartActive,
          })}
          onClick={onChart}
        >
          <ChartIcon className={styles.icon20} />
          {chartLabel}
        </button>
        <button
          type="button"
          className={cs(styles.button, styles.buttonPrimary)}
          onClick={onKeywords}
        >
          <PlusIcon className={styles.icon20} />
          {keywordsLabel}
        </button>
      </div>
    </div>
  );
};

export default AppInsightsControls;
